import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { isEmpty, size } from "lodash";
import { createErrorSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { getUserRollovers } from "actions/adminActions";
import { Card } from "react-bootstrap";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import IconMessageCard from "components/IconMessageCard";
import IconSpinner from "components/IconSpinner";
import Alert from "components/Alert";
import { formatCurrency } from "utils/number";
import moment from "moment";
import type { RootState } from "types/state";
import type { UserRolloversQuery } from "types/graphql";

// A single rollover row, as selected by the userRollovers query.
type RolloverRow = NonNullable<
  NonNullable<UserRolloversQuery["userRollovers"]>[number]
>;

const errorSelector = createErrorSelector(adminConstants.GET_USER_ROLLOVERS);

const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
});

const mapDispatchToProps = {
  getUserRollovers,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// userId is passed in by the parent (the user-detail page), not from redux.
interface OwnProps {
  userId?: string;
}

type Props = WithApolloClient<PropsFromRedux & OwnProps>;

interface State {
  rollovers: RolloverRow[];
  isFetching: boolean;
}

class UserRollovers extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      rollovers: [],
      isFetching: true,
    };
  }

  async componentDidMount() {
    await this._getAndSetRollovers();
    this.setState({
      isFetching: false,
    });
  }

  _getAndSetRollovers = async () => {
    // The dispatched action is the SUCCESS action ({ type, data }) on the
    // happy path; `data` is the userRollovers payload. The FAILURE branch
    // returns an action without `data`, leaving rollovers undefined — matching
    // the original JS runtime behavior.
    const { data: rollovers } = (await this.props.getUserRollovers(
      this.props.client as ApolloClient<object>,
      // userId is optional on this component but the query variable is
      // required; the original JS passed it straight through (may be undefined
      // at runtime). Preserve that pass-through with a boundary cast.
      this.props.userId as string
    )) as { data?: RolloverRow[] };

    this.setState({ rollovers: rollovers as RolloverRow[] });
  };

  _getColumns(): Array<IconTableColumn<RolloverRow>> {
    return [
      {
        label: "Account Number",
        key: "accountNumber",
      },
      {
        label: "Amount",
        key: "amount",
        format: (amount: unknown) => {
          if (!amount && amount !== 0) {
            return "Unknown";
          }
          return formatCurrency(amount as number);
        },
      },
      {
        label: "Status",
        key: "status",
      },
      {
        label: "Origination Source",
        key: "originationSource",
      },
      {
        label: "Transfer Method",
        key: "fundsTransferMethod",
      },
      {
        label: "Last Updated",
        key: "updatedAt",
        format: (updatedAt: unknown) =>
          moment(updatedAt as moment.MomentInput).format("YYYY-MM-DD"),
      },
    ];
  }

  render() {
    const { error } = this.props;
    const { isFetching, rollovers } = this.state;

    if (isFetching) {
      return <IconSpinner centered />;
    }
    if (error) {
      return (
        // @ts-expect-error Alert ignores children (renders its `msg` prop); the
        // original JS passed `{error}` as inert children — preserved unchanged.
        <Alert type="error">{error}</Alert>
      );
    }

    if (isEmpty(rollovers)) {
      return (
        <IconMessageCard header="Rollovers" subheader="No rollovers found" />
      );
    }

    return (
      <Card>
        <Card.Body>
          <IconTableHeader
            // @ts-expect-error IconTableHeader has no `title` prop; it is
            // ignored by the component. Preserving the original inert prop.
            title="Rollovers"
            tableHeader="Rollovers"
            tableCountLabel="Total"
            tableCount={size(rollovers)}
          />
          <IconTable columns={this._getColumns()} data={rollovers} />
        </Card.Body>
      </Card>
    );
  }
}

export default connector(withApollo<Props>(UserRollovers));
